// src/pages/Administration/Users/UserDetailModal.tsx
/**
 * The detail popup behind every row, card and table line on `/Administration/Users`.
 *
 * Exists mostly for the **user id**. It is the join key between Identity and the `Leader` /
 * `Facilitator` records, so it is what an admin needs when reconciling someone who appears in one place
 * and not the other, and it is the thing they will be asked for when reporting a problem. It is shown in
 * full with a copy button rather than truncated.
 *
 * Changing roles is reached from here rather than from the list. Confirming who you are looking at
 * before editing their access is worth the extra click.
 */
import { useState } from 'react';
import { IconCheck, IconCopy, IconShieldCog } from '@tabler/icons-react';
import {
  Avatar, Badge, Box, Button, Code, Divider, Group, Modal, Stack, Text,
} from '@/components/ui';
import type { UserModelView } from '@actschurch/ccms-sdk';
import { getInitialsFromName, resolveAvatarSrc } from '@/utils/avatar';

export interface UserDetailModalProps {
  user: UserModelView | null;
  onClose: () => void;
  /** Opens the roles drawer for this person. */
  onChangeRoles: (user: UserModelView) => void;
  /** Renders the person's Acts Care role badges, so the page owns that vocabulary. */
  renderRoles: (user: UserModelView) => React.ReactNode;
  displayName: (user: UserModelView) => string;
}

/** One label/value line. Values that are absent read as "Not recorded" rather than showing blank. */
function DetailRow({ label, value }: { label: string; value?: React.ReactNode }) {
  const empty = value === null || value === undefined || value === '';
  return (
    <Group justify="space-between" align="flex-start" wrap="nowrap" gap="md">
      <Text size="sm" c="dimmed" style={{ flexShrink: 0 }}>{label}</Text>
      <Box style={{ textAlign: 'right', minWidth: 0 }}>
        {empty ? <Text size="sm" c="dimmed">Not recorded</Text> : value}
      </Box>
    </Group>
  );
}

function YesNo({ value }: { value?: boolean | null }) {
  if (value === null || value === undefined) return <Text size="sm" c="dimmed">Unknown</Text>;
  return (
    <Badge size="sm" variant="light" color={value ? 'green' : 'gray'}>
      {value ? 'Yes' : 'No'}
    </Badge>
  );
}

export function UserDetailModal({
  user,
  onClose,
  onChangeRoles,
  renderRoles,
  displayName,
}: UserDetailModalProps) {
  const [copied, setCopied] = useState(false);

  const copyId = async () => {
    if (!user?.id) return;
    try {
      await navigator.clipboard.writeText(user.id);
      setCopied(true);
      window.setTimeout(() => setCopied(false), 1500);
    } catch {
      // Clipboard is blocked on insecure origins and in some browsers. The id is fully visible above,
      // so failing quietly is fine; the admin can still select it.
      setCopied(false);
    }
  };

  const name = user ? displayName(user) : '';

  return (
    <Modal opened={Boolean(user)} onClose={onClose} title="User details" size="md" centered>
      {user && (
        <Stack gap="md">
          <Group gap="md" wrap="nowrap">
            <Avatar src={resolveAvatarSrc(user.avatar)} alt={name} radius="xl" size={64}>
              {getInitialsFromName(name)}
            </Avatar>
            <Box style={{ minWidth: 0 }}>
              <Text fw={600}>{name}</Text>
              <Text size="sm" c="dimmed" truncate>{user.email || 'No email on record'}</Text>
              <Group gap={4} mt={6} wrap="wrap">{renderRoles(user)}</Group>
            </Box>
          </Group>

          <Divider />

          <Stack gap="xs">
            <DetailRow
              label="User ID"
              value={user.id ? (
                <Group gap={6} wrap="nowrap" justify="flex-end">
                  <Code style={{ wordBreak: 'break-all' }}>{user.id}</Code>
                  <Button
                    size="compact-xs"
                    variant="subtle"
                    color={copied ? 'green' : 'gray'}
                    onClick={copyId}
                    leftSection={copied ? <IconCheck size={12} /> : <IconCopy size={12} />}
                  >
                    {copied ? 'Copied' : 'Copy'}
                  </Button>
                </Group>
              ) : undefined}
            />
            <DetailRow
              label="Name"
              value={[user.firstName, user.surname].filter(Boolean).join(' ') || undefined}
            />
            <DetailRow label="Username" value={user.userName} />
            <DetailRow label="Cellphone" value={user.cellphone} />
            <DetailRow label="Gender" value={user.gender} />
          </Stack>

          <Divider />

          <Stack gap="xs">
            <DetailRow label="Member profile" value={<YesNo value={user.isMember} />} />
            <DetailRow label="Email confirmed" value={<YesNo value={user.emailConfirmed} />} />
            <DetailRow
              label="Locked out"
              value={
                <Badge size="sm" variant="light" color={user.isLockedOut ? 'red' : 'green'}>
                  {user.isLockedOut ? 'Yes' : 'No'}
                </Badge>
              }
            />
            <DetailRow label="Created" value={user.createdDate} />
            <DetailRow
              label="Roles in all of CCMS"
              value={<Text size="sm">{user.totalRoles ?? (user.roles?.length ?? 0)}</Text>}
            />
          </Stack>

          <Group justify="flex-end" mt="sm">
            <Button variant="subtle" onClick={onClose}>Close</Button>
            <Button
              leftSection={<IconShieldCog size={16} />}
              onClick={() => { onChangeRoles(user); onClose(); }}
            >
              Change roles
            </Button>
          </Group>
        </Stack>
      )}
    </Modal>
  );
}

export default UserDetailModal;
